import React, { useState, useEffect, useCallback } from 'react'
import { Box, Button, InputLabel, CircularProgress, Alert, Typography } from '@mui/material'
import { RenderField } from '../../../components/Field'
import { useParams } from 'react-router-dom'
import { useGetEmailExists, useGetIdNumberExists } from './hooks'
import debounce from 'lodash.debounce'
import { useGetListItems } from '../hooks'

type WorkerInfoPropTypes = {
  saveWorker: (data: any) => void
  workerData: null | object
  disabled: boolean
  workerPhotoRaw: any
}

export const WorkerInfoFormNew = (props: WorkerInfoPropTypes) => {
  const { saveWorker, workerData, disabled, workerPhotoRaw } = props
  const { id } = useParams()
  const [formData, setFormData] = useState<any | undefined>({})
  const [workerEmail, setWorkerEmail] = useState<any | undefined>(null)
  const [workerIdNumber, setWorkerIdNumber] = useState<any | undefined>(null)
  const [preventSave, setPreventSave] = useState<boolean>(false)
  const { data: emailExists, isFetching: isEmailExistsFetching,refetch } =
    useGetEmailExists('email', workerEmail)
  const { data: idNumberExists, isFetching: isIdNumberExistsFetching } =
    useGetIdNumberExists('idNumber', workerIdNumber)
  const [errorMessage, setErrorMessage] = useState<any | undefined>('')

  useEffect(() => {
    refetch();
  }, [emailExists, formData, workerPhotoRaw, idNumberExists]); 
  

  const fields: any[] = [
    {
      fieldType: 'input',
      name: 'email',
      label: 'Email',
      helperText: 'john@email.com',
      errorMessage: 'Oops, error with email',
      isRequired: false,
      rules: null,
    },
    {
      fieldType: 'input',
      name: 'firstName',
      label: 'First Name',
      helperText: 'Juan',
      errorMessage: 'Oops, error with first name.',
      isRequired: true,
      rules: null,
    },
    {
      fieldType: 'input',
      name: 'lastName',
      label: 'Last Name',
      helperText: 'Garcia',
      errorMessage: 'Oops, error with last name.',
      isRequired: true,
      rules: null,
    },
    {
      fieldType: 'input',
      name: 'idNumber',
      label: 'ID Number',
      helperText: '',
      errorMessage: 'Oops, error with ID Number.',
      isRequired: true,
      rules: null,
    },
  ]

  const handleSearchingEmail = useCallback(
    debounce((email: string) => {
      setWorkerEmail(email)
    }, 500),
    []
  )

  const handleSearchingId = useCallback(
    debounce((id: string) => {
      setWorkerIdNumber(id)
    }, 500),
    []
  )

  const validateEmail = (email: string) => {
    const emailRegex = /^(?!.*?[.]{2})[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

    if (email === '' || emailRegex.test(email.trim())) {
      setErrorMessage('')
      return true
    } else {
      setErrorMessage('Please  provide a valid Email address')
      return false
    }
  }

  const onDataEntry = (e: any) => {
    const { name, value } = e.target;
    let trimmedValue = value.trim();
    if (name === 'idNumber') {
      trimmedValue = trimmedValue.substring(0, 20);  }
    if (name === 'firstName') {
      trimmedValue = trimmedValue.substring(0, 15);  }
    if (name === 'lastName') {
      trimmedValue = trimmedValue.substring(0, 15);  }
    setFormData((formData: any) => ({ ...formData, [name]: trimmedValue }));
    if (name === 'email' && validateEmail(trimmedValue)) {
      handleSearchingEmail(trimmedValue);
    }
    if (name === 'idNumber') {
      handleSearchingId(trimmedValue);
    }
  };

  const handleSave = () => {
    props.saveWorker(formData)
  }

  useEffect(() => {
    if (emailExists?.exists || idNumberExists?.exists) {
      setPreventSave(true)
    } else if (!emailExists?.exists || !idNumberExists?.exists) {
      setPreventSave(false)
    }
    // if formData is an empty object, then set preventSave to true
    if (Object.keys(formData).length === 0) {
      setPreventSave(true)
    }
    // if formData has empty required fields, then set preventSave to true
    fields.forEach(field => {
      if (field.isRequired && !formData[field.name]) {
        setPreventSave(true)
      }
    })
    if (!workerPhotoRaw) {
      setPreventSave(true)
    }
    // if email is present in formData, but invalid, then set preventSave to true
    formData.email = formData?.email?.trim();
    if (formData.email && !validateEmail(formData.email)) {
      setPreventSave(true)
    }
  }, [emailExists, formData, workerPhotoRaw, idNumberExists])

  console.log("idNumberExists?.exists", idNumberExists)

  return (
    <>
      <form>
        {fields.map((field, key) => (
          <Box key={key} className='formfield'>
            <InputLabel sx={{ display: 'block', fontWeight: 'bold' }}>
              {field.label}
            </InputLabel>
            {(field.name === 'email' && isEmailExistsFetching) || (field.name === 'idNumber' && isIdNumberExistsFetching) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem',
                }}>
                <CircularProgress size='1rem' />
                <div style={{ marginLeft: '0.5rem' }}>Verifying {field.name}...</div>
              </div>
            )}
            {field.name === 'email' && emailExists?.exists ? (
              <Alert severity='error' sx={{ mt: 1 }}>This Email is already registered</Alert>

            ) : (
              errorMessage && field.name === 'email' && <Alert severity='error' sx={{ mt: 1 }}>{errorMessage}</Alert>

            )}    
            {RenderField(field, key, onDataEntry, formData[field.name])}
            {field.name === 'idNumber' && idNumberExists?.exists && (
              <Alert severity='error' sx={{ marginBottom: 2 }}>
                This ID Number is already registered
              </Alert>
            )}
          </Box>
        ))}
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={
            disabled ||
            isEmailExistsFetching ||
            emailExists?.exists ||
            preventSave || idNumberExists?.exists
          }
        >
          {!id ? 'Enroll Worker' : 'Update Worker'}
        </Button>
      </form>
    </>
  )
}
